/**
 * Vault — detalle de una credencial (F3a): revelar por 30 s, TOTP, URL, notas,
 * quién tiene acceso, historial de versiones, actividad reciente; editar, rotar, compartir
 * por link, borrar y el asistente para cargar el MFA.
 *
 * El badge dice la verdad de la Opción A: "Cifrado con AWS KMS · el servidor
 * puede descifrar" (el mockup decía "descifrado en tu navegador").
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, Link2, Lock, Pencil, RotateCw, ShieldAlert, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, LoaderBlock } from '@/components/internal/shared';
import { ActivityList, AreaBadge, MfaBadge, PermissionBadge } from '@/components/vault/vault-ui';
import { RevealField } from '@/components/vault/RevealField';
import { TotpField } from '@/components/vault/TotpField';
import { useUnlockPrompt } from '@/components/vault/UnlockDialog';
import { GrantsPanel } from '@/components/vault/GrantsPanel';
import { HistoryPanel } from '@/components/vault/HistoryPanel';
import { EditSecretDialog } from '@/components/vault/EditSecretDialog';
import { RotateDialog } from '@/components/vault/RotateDialog';
import { ShareLinkDialog } from '@/components/vault/ShareLinkDialog';
import { DeleteSecretButton } from '@/components/vault/DeleteSecretButton';
import { MfaAssistant } from '@/components/vault/MfaAssistant';
import { errMsg, useCopy, useVaultAccess, useVaultMutations, withUnlockRetry } from '@/hooks/useVault';
import { VAULT_POLICY, parsePrincipal } from '../../../../shared/types/vault';

export function SecretDetailPage() {
  const { t } = useTranslation();
  const { secretId = '' } = useParams();
  const copy = useCopy();
  const access = useVaultAccess();
  const detail = trpc.vault.getSecret.useQuery({ secretId }, { enabled: !!secretId, staleTime: 30_000 });
  const { revealSecret } = useVaultMutations();
  const { ensureUnlocked, dialog } = useUnlockPrompt();
  const [revealed, setRevealed] = useState<{ password: string; notes: string | null } | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);

  const myAreas = useMemo(
    () => (access.data?.principals ?? []).map(parsePrincipal).filter((p): p is { kind: 'area'; group: string } => !!p && p.kind === 'area').map((p) => p.group),
    [access.data]
  );

  const reveal = async () => {
    setRevealError(null);
    try {
      const res = await withUnlockRetry(ensureUnlocked, () => revealSecret.mutateAsync({ secretId }));
      if (res) setRevealed(res);
    } catch (e) {
      setRevealError(errMsg(e as { message?: string }));
    }
  };

  if (detail.isLoading) return <LoaderBlock />;
  if (!detail.data) return <EmptyState title={t('vault.detail.not_found', 'La credencial no existe o no tenés acceso')} />;

  const { meta, grants, hasNotes, totp, recentActivity, rotationDueInDays } = detail.data;
  const canRead = meta.myPermission !== null;
  const canEdit = meta.myPermission === 'edit' || meta.myPermission === 'owner';
  const canOwn = meta.myPermission === 'owner';

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-2 sm:p-4">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/vault" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          {t('vault.detail.back', 'Volver')}
        </Link>
        <span>/</span>
        <Link to="/vault" className="hover:text-foreground">{t('vault.nav.my_secrets', 'Mis credenciales')}</Link>
        <span>/</span>
        <span className="text-foreground">{meta.name}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">{meta.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <AreaBadge area={meta.area} />
            {meta.clientId && <Badge variant="outline">{t('vault.detail.client', 'Cliente: {{c}}', { c: meta.clientId })}</Badge>}
            <span data-tour="mfa-badge"><MfaBadge hasTotp={meta.hasTotp} /></span>
            {canEdit && (
              <Button variant={meta.hasTotp ? 'ghost' : 'secondary'} size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setMfaOpen(true)} data-tour="mfa-assistant">
                <Sparkles className="h-3.5 w-3.5 text-brand-green" />
                {meta.hasTotp ? t('vault.mfa.reconfigure', 'Reconfigurar MFA') : t('vault.mfa.load', 'Cargar el MFA con ayuda')}
              </Button>
            )}
            <PermissionBadge permission={meta.myPermission} />
            {/* reuse-ok: el badge de rotación lleva ícono + data-tour del workshop; EnumBadge no admite hijos */}
            {meta.needsRotation && (
              <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300" data-tour="needs-rotation" title={t('vault.detail.rotation_hint', 'Sugerencia, no obligación: el owner decide cuándo rotarla.')}>
                <ShieldAlert className="h-3 w-3" />
                {t('vault.detail.needs_rotation', 'Rotación sugerida')}
              </Badge>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setShareOpen(true)} data-tour="share">
              <Link2 className="mr-2 h-4 w-4" />
              {t('vault.share.button', 'Compartir por link')}
            </Button>
            <Button variant="outline" onClick={() => setRotateOpen(true)} data-tour="rotate">
              <RotateCw className="mr-2 h-4 w-4" />
              {t('vault.detail.rotate', 'Rotar')}
            </Button>
            <Button onClick={() => setEditOpen(true)} data-tour="edit">
              <Pencil className="mr-2 h-4 w-4" />
              {t('vault.detail.edit', 'Editar')}
            </Button>
            {canOwn && <DeleteSecretButton secretId={secretId} name={meta.name} ensureUnlocked={ensureUnlocked} />}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <Card className="divide-y">
          <div className="flex items-center justify-between p-4">
            <h2 className="font-semibold">{t('vault.detail.credential', 'Credencial')}</h2>
            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" />
              {t('vault.detail.kms_badge', 'Cifrado con AWS KMS · el servidor puede descifrar')}
            </span>
          </div>

          <div className="space-y-1 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.detail.username', 'Usuario')}</div>
            <div className="flex items-center gap-3">
              <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm">{meta.username}</code>
              <Button variant="outline" size="icon" onClick={() => copy(meta.username, t('vault.detail.username_copied', 'Usuario copiado'))} title={t('vault.reveal.copy', 'Copiar')} aria-label={t('vault.reveal.copy', 'Copiar')}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-1 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.detail.password', 'Contraseña')}</div>
            {canRead ? (
              <>
                <RevealField value={revealed?.password ?? null} loading={revealSecret.isPending} onReveal={reveal} onHide={() => setRevealed(null)} />
                {revealError && <p className="text-xs text-destructive">{revealError}</p>}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('vault.detail.admin_no_grant', 'Ves esta credencial como admin, pero sin un acceso otorgado no se puede revelar.')}</p>
            )}
          </div>

          {totp && canRead && (
            <div className="space-y-1 p-4" data-tour="totp">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.detail.totp', 'Código MFA (TOTP)')}</div>
              <TotpField secretId={secretId} digits={totp.digits} step={totp.step} ensureUnlocked={ensureUnlocked} />
            </div>
          )}

          {meta.url && (
            <div className="space-y-1 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">URL</div>
              <a href={meta.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-sm text-sky-600 hover:underline">
                {meta.url.replace(/^https?:\/\//, '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {hasNotes && canRead && (
            <div className="space-y-1 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.detail.notes', 'Notas')}</div>
              {revealed ? (
                <p className="whitespace-pre-wrap text-sm">{revealed.notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground">{t('vault.detail.notes_hidden', 'Se muestran al revelar la contraseña (están cifradas).')}</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 p-4 text-xs text-muted-foreground">
            <span className={`rounded-full px-2 py-0.5 ${rotationDueInDays <= 14 ? 'bg-amber-50 text-amber-700' : 'bg-muted'}`}>
              {rotationDueInDays <= 0
                ? t('vault.detail.rotation_overdue', 'Sugerimos rotarla')
                : t('vault.detail.rotation_in', 'Rotación sugerida en {{d}} días', { d: rotationDueInDays })}
            </span>
            <span>
              {t('vault.detail.rotation_policy', 'Sugerencia: rotar cada {{d}} días, y también si se revoca un acceso. No es obligatorio.', { d: VAULT_POLICY.rotationDays })}
            </span>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-4" data-tour="grants">
            <h2 className="mb-3 font-semibold">{t('vault.detail.who', 'Quién tiene acceso')}</h2>
            <GrantsPanel secretId={secretId} grants={grants} canOwn={canOwn} />
            <p className="mt-3 text-xs text-muted-foreground">{t('vault.detail.owner', 'Owner: {{o}}', { o: meta.ownerEmail })}</p>
          </Card>
          {canRead && (
            <Card className="p-4" data-tour="history">
              <h2 className="mb-3 font-semibold">{t('vault.detail.history', 'Historial de contraseñas')}</h2>
              <HistoryPanel secretId={secretId} ensureUnlocked={ensureUnlocked} />
            </Card>
          )}
          <Card className="p-4" data-tour="activity">
            <h2 className="mb-3 font-semibold">{t('vault.detail.activity', 'Actividad reciente')}</h2>
            <ActivityList items={recentActivity} emptyLabel={t('vault.detail.no_activity', 'Sin actividad todavía.')} />
          </Card>
        </div>
      </div>
      {canEdit && (
        <>
          <EditSecretDialog open={editOpen} onOpenChange={setEditOpen} meta={meta} hasNotes={hasNotes} isAdmin={!!access.data?.isAdmin} myAreas={myAreas} ensureUnlocked={ensureUnlocked} />
          <RotateDialog open={rotateOpen} onOpenChange={setRotateOpen} secretId={secretId} version={meta.version} ensureUnlocked={ensureUnlocked} />
          <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} secretId={secretId} canOwn={canOwn} hasTotp={!!totp} ensureUnlocked={ensureUnlocked} />
          <MfaAssistant open={mfaOpen} onOpenChange={setMfaOpen} secretId={secretId} secretName={meta.name} version={meta.version} hasTotp={meta.hasTotp} ensureUnlocked={ensureUnlocked} />
        </>
      )}
      {dialog}
    </div>
  );
}
